/**
 * Component Registry for Aion Management Theme
 *
 * Extends the base comfy theme registry with Aion Management specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-comfy/registry';
import { AccessibilityStatement } from './components/AccessibilityStatement';
import { AmenitiesList } from './components/AmenitiesList';
import { ApartmentSyncSection } from './components/ApartmentSyncSection';
import { ContactForm } from './components/ContactForm';
import { ContactMap } from './components/ContactMap';
import { FeeSheet } from './components/FeeSheet';
import { FloorplansList } from './components/FloorplansList';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { InteractiveMapSection } from './components/InteractiveMapSection';
import { Hero } from './components/Hero';
import { HeroSolidBG } from './components/HeroSolidBG';
import { HyperlocalHero } from './components/HyperlocalHero';
import { HyperlocalPageCopy } from './components/HyperlocalPageCopy';
import { HomeGalleryPreview } from './components/HomeGalleryPreview';
import { NeighborhoodHighlights } from './components/NeighborhoodHighlights';
import { NeighborhoodMap } from './components/NeighborhoodMap';
import { PetPolicy } from './components/PetPolicy';
import { PhotoGallery } from './components/PhotoGallery';
import { PropertyOverview } from './components/PropertyOverview';
import { SitemapContent } from './components/SitemapContent';
import { ThankYouContent } from './components/ThankYouContent';
import { VirtualTourEmbed } from './components/VirtualTourEmbed';

// Added by aion-management: appended at the end so it doesn't collide with
// concurrent alphabetical inserts from main during automatic 3-way merges.
import { ResidentAccess } from './components/ResidentAccess';
import { SpecialOfferBanner } from './components/SpecialOfferBanner';

/**
 * Aion Management specific component overrides.
 * These components have been customized for the Aion Management brand.
 */
const aionmanagementOverrides: Partial<ComponentRegistry> = {
  AccessibilityStatement,
  AmenitiesList,
  ApartmentSyncSection,
  ContactForm,
  ContactMap,
  FeeSheet,
  FloorplansList,
  Footer,
  Header,
  Hero,
  HeroSolidBG,
  HyperlocalHero,
  HyperlocalPageCopy,
  InteractiveMapSection,
  HomeGalleryPreview,
  NeighborhoodHighlights,
  NeighborhoodMap,
  PetPolicy,
  PhotoGallery,
  PropertyOverview,
  SitemapContent,
  SpecialOfferBanner,
  ThankYouContent,
  VirtualTourEmbed,
};

/**
 * Final merged registry for Aion Management properties.
 * Inherits all components from the comfy theme, with Aion Management overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...aionmanagementOverrides,
  ResidentAccess,
};

export default registry;
